import React, { useState } from "react";
import { Routes, Route} from "react-router-dom";
import {
  AppBar,
  Toolbar,
  Box,
  IconButton,
  CssBaseline,
  ThemeProvider,
  createTheme,
  Typography,
  Button,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
} from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import LoginIcon from "@mui/icons-material/Login";
import LogoutIcon from "@mui/icons-material/Logout";
import Brightness4Icon from "@mui/icons-material/Brightness4";
import Brightness7Icon from "@mui/icons-material/Brightness7";
import { getAuth, signOut } from "firebase/auth";
import { useAuth } from "./context/AuthContext"; // Import authentication hook
import { Link, useLocation } from "react-router-dom";
import ProtectedRoute from "./components/ProtectedRoute";
import LoginPage from "./LoginPage";
import DashboardPage from "./DashboardPage";
import DashboardLayout from "./DashboardLayout";
import HomePage from "./HomePage";
import TemplatePage from "./TemplatePage";
import PricingPage from "./PricingPage";
import TutorialPage from "./TutorialPage";
import InvitationDisplay from './components/InvitationDisplay';

const auth = getAuth();
export const handleLogout = async () => {
  try {
    await signOut(auth);
  } catch (error) {
    console.error("Logout failed:", error.message);
  }
};

const Page = () => {
  const [darkMode, setDarkMode] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false); // State for mobile menu
  const { user } = useAuth(); // Get user state

  const baseUrl = process.env.PUBLIC_URL || "";

  const theme = createTheme({
    palette: {
      mode: darkMode ? "dark" : "light",
    },
  });

  const location = useLocation();
  const isDashboardPage = location.pathname.startsWith("/app/"); // Detect Dashboard

  // Function to get the correct link color
  const getLinkColor = (path) => {
    if (location.pathname === path) {
      return "#e22728"; // Active link color
    }
    return darkMode ? "white" : "black"; // Default link color based on mode
  };

  // Navigation Links
  const navLinks = [
    { label: "Home", path: "/" },
    { label: "Template", path: "/templates" },
    { label: "Pricing", path: "/pricing" },
    { label: "Tutorial", path: "/tutorial" },
  ];

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
        <Routes>
          {/* Dashboard routes */}
          <Route
            path="/app/*"
            element={
              <DashboardLayout>
                <ProtectedRoute>
                  <Routes>
                    <Route path="/*" element={<DashboardPage />} />
                  </Routes>
                </ProtectedRoute>
              </DashboardLayout>
            }
          />
          <Route
            path="/*"
            element={
            <Box display="flex" flexDirection="column" minHeight="100vh">
            <AppBar position="static" sx={{ bgcolor: "inherit" }}>
              <Toolbar>
                {/* Logo Section */}
                <Box display="flex" alignItems="center">
                  <img
                    src={`${baseUrl}/android-chrome-512x512.png`}
                    alt="Logo"
                    style={{ height: 50 }}
                  />
                  <Typography
                    variant="h6"
                    sx={{
                      ml: 1,
                      fontWeight: "bold",
                      color: (theme) =>
                        theme.palette.mode === "dark" ? "white" : "black",
                    }}
                  >
                    ECARDLY.PRO
                  </Typography>
                </Box>

                {/* Mobile Menu Button (Hamburger) */}
                <IconButton
                  edge="end"
                  onClick={() => setDrawerOpen(true)}
                  sx={{ display: { xs: "flex", md: "none" }, ml: "auto" }}
                >
                  <MenuIcon />
                </IconButton>

                {/* Desktop Navigation Links */}
                <Box
                  display={{ xs: "none", md: "flex" }}
                  alignItems="center"
                  gap={3}
                  sx={{ ml: "auto" }}
                >
                  {navLinks.map((link) => (
                    <Link
                      key={link.path}
                      to={link.path}
                      style={{
                        textDecoration: "none",
                        color: getLinkColor(link.path),
                      }}
                    >
                      {link.label}
                    </Link>
                  ))}

                  {/* Dark Mode Toggle */}
                  <IconButton
                    onClick={() => setDarkMode(!darkMode)}
                    sx={{ color: darkMode ? "white" : "black" }}
                  >
                    {darkMode ? <Brightness7Icon /> : <Brightness4Icon />}
                  </IconButton>

                  {/* Login / Logout Button */}
                  {user ? (
                    <Button
                      variant="contained"
                      onClick={handleLogout}
                      sx={{ backgroundColor: "red", color: "white", borderRadius: "20px", px: 3 }}
                    >
                      <LogoutIcon sx={{ mr: 1 }} />
                      Logout
                    </Button>
                  ) : (
                    <Button
                      component={Link}
                      to="/login"
                      variant="contained"
                      sx={{ backgroundColor: "blue", color: "white", borderRadius: "20px", px: 3 }}
                    >
                      <LoginIcon sx={{ mr: 1 }} />
                      Login
                    </Button>
                  )}
                </Box>
              </Toolbar>
            </AppBar>

            {/* Mobile Drawer Menu */}
            <Drawer
              anchor="right"
              open={drawerOpen}
              onClose={() => setDrawerOpen(false)}
            >
              <Box
                sx={{
                  width: 250,
                  height: "100%",
                  bgcolor: darkMode ? "#121212" : "#fff",
                  color: darkMode ? "white" : "black",
                }}
              >
                <List>
                  {navLinks.map((link) => (
                    <ListItem key={link.path} disablePadding>
                      <ListItemButton component={Link} to={link.path} onClick={() => setDrawerOpen(false)}>
                        <ListItemText
                          primary={link.label}
                          sx={{ color: getLinkColor(link.path) }}
                        />
                      </ListItemButton>
                    </ListItem>
                  ))}
                  <ListItem disablePadding>
                    <ListItemButton onClick={() => setDarkMode(!darkMode)}>
                      <ListItemText primary="Toggle Dark Mode" />
                      {darkMode ? <Brightness7Icon /> : <Brightness4Icon />}
                    </ListItemButton>
                  </ListItem>
                  <ListItem disablePadding>
                    {user ? (
                      <ListItemButton onClick={handleLogout}>
                        <LogoutIcon sx={{ mr: 1 }} />
                        <ListItemText primary="Logout" />
                      </ListItemButton>
                    ) : (
                      <ListItemButton component={Link} to="/login" onClick={() => setDrawerOpen(false)}>
                        <LoginIcon sx={{ mr: 1 }} />
                        <ListItemText primary="Login" />
                      </ListItemButton>
                    )}
                  </ListItem>
                </List>
              </Box>
            </Drawer>

            {/* Main Content */}
            <Box
              display="flex"
              flex={1}
              alignItems="center"
              justifyContent="center"
              flexDirection="column"
              sx={{
                backgroundImage: "url('/assets/rextifire_vector_design.svg')",
                backgroundSize: "cover",
                backgroundPosition: "center",
                width: "100%",
                minHeight: "100vh",
              }}
            >
              <Routes>
                <Route path="/" element={<HomePage />} /> 
                <Route path="/login" element={<LoginPage />} />
                <Route path="/templates" element={<TemplatePage />} />
                <Route path="/pricing" element={<PricingPage />} />
                <Route path="/tutorial" element={<TutorialPage />} />
                {/* Dashboard uses a separate layout */}
                <Route
                  path="/app/*"
                  element={
                    <ProtectedRoute>
                      <Routes>
                        <Route path="/*" element={<DashboardPage />} />
                      </Routes>
                    </ProtectedRoute>
                  }
                />
              </Routes>
            </Box>

            {/* Footer */}
            <Box bgcolor="inherit" color="primary" p={2} textAlign="center">
              Copyright Reserved © {new Date().getFullYear()} Rextifire Global
            </Box>
          </Box>
          }
        />
        {/* Full screen invitation display route */}
        {/* <Route path="/:route" element={<InvitationDisplay />} /> */}
      </Routes>
    </ThemeProvider>
  );
};

export default Page;
